import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { ArrowUpRight, LoaderCircle, type LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn.js';

export type ButtonVariant = 'primary' | 'tertiary' | 'ghost';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 12px Lucide icon before the label. */
  icon?: LucideIcon;
  /** Ghost only: trailing `→` (default true for ghost). */
  trailingArrow?: boolean;
  /** Ghost only: use ArrowUpRight (opens elsewhere) instead of `→`. */
  external?: boolean;
  /** Replaces the icon with a spinner and blocks clicks. Label stays visible. */
  loading?: boolean;
  children?: ReactNode;
}

const BASE =
  'inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-hi disabled:hover:bg-brand aria-disabled:hover:bg-brand',
  tertiary:
    'border border-line bg-panel text-ink hover:bg-card disabled:hover:bg-panel aria-disabled:hover:bg-panel',
  ghost: 'font-mono uppercase tracking-[0.2em] text-mute hover:text-ink group',
};

const SIZE: Record<ButtonSize, Record<ButtonVariant, string>> = {
  md: { primary: 'px-3 py-1.5 text-xs', tertiary: 'px-3 py-1.5 text-xs', ghost: 'py-1 text-2xs' },
  sm: { primary: 'px-2.5 py-1 text-xs', tertiary: 'px-2.5 py-1 text-xs', ghost: 'py-0.5 text-2xs' },
};

/**
 * Buttons (DESIGN_SYSTEM section 8.2). Three variants, all small. One primary per page header.
 * Ghost is the mono link style with a trailing arrow.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'tertiary',
    size = 'md',
    icon: Icon,
    trailingArrow,
    external = false,
    loading = false,
    disabled,
    className,
    children,
    type = 'button',
    onClick,
    ...rest
  },
  ref,
) {
  const isDisabled = Boolean(disabled) || loading;
  const showArrow = variant === 'ghost' && (trailingArrow ?? true);
  return (
    <button
      ref={ref}
      type={type}
      className={cn(BASE, VARIANT[variant], SIZE[size][variant], className)}
      disabled={disabled}
      aria-disabled={loading ? true : undefined}
      aria-busy={loading ? true : undefined}
      onClick={isDisabled ? (e) => e.preventDefault() : onClick}
      {...rest}
    >
      {loading ? (
        <LoaderCircle size={12} className="motion-safe:animate-spin" aria-hidden />
      ) : Icon ? (
        <Icon size={12} aria-hidden />
      ) : null}
      {children}
      {showArrow ? (
        external ? (
          <ArrowUpRight
            size={11}
            aria-hidden
            className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand-text motion-reduce:transition-none motion-reduce:group-hover:translate-0"
          />
        ) : (
          <span aria-hidden>→</span>
        )
      ) : null}
    </button>
  );
});
